<template>
  <div class="management-shell">
    <a class="skip-link" href="#management-main">跳至主要内容</a>
    <aside class="management-sidebar" :class="{ open: drawerOpen }">
      <div class="sidebar-brand">
        <img src="../../assets/logo_dark_bg.svg" alt="地脉动监测预警系统" class="sidebar-brand-logo" />
        <button class="icon-button sidebar-close" type="button" aria-label="关闭导航" @click="drawerOpen = false">
          <X :size="20" />
        </button>
      </div>

      <nav aria-label="管理平台主导航">
        <RouterLink
          v-for="item in navItems"
          :key="item.name"
          :to="{ name: item.name }"
          class="nav-item"
          :class="{ 'nav-item-active': isNavItemActive(item.name) }"
        >
          <component :is="item.icon" :size="19" aria-hidden="true" />
          <span>{{ item.label }}</span>
          <span v-if="item.badge" class="nav-badge">{{ item.badge }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer">
        <RouterLink :to="{ name: 'Dashboard' }" class="nav-item">
          <Map :size="19" aria-hidden="true" />
          <span>返回监测首页</span>
        </RouterLink>
      </div>
    </aside>
    <button v-if="drawerOpen" class="drawer-backdrop" type="button" aria-label="关闭导航" @click="drawerOpen = false"></button>

    <div class="management-workspace">
      <header class="management-topbar">
        <button class="icon-button menu-toggle" type="button" aria-label="打开导航" @click="drawerOpen = true">
          <Menu :size="21" />
        </button>
        <div class="topbar-title">
          <span>地脉动监测预警系统</span>
          <strong>{{ routeTitle }}</strong>
        </div>
        <div class="topbar-actions">
          <button class="icon-button" type="button" :aria-label="themeLabel" :title="themeLabel" @click="themeStore.toggle()">
            <Sun v-if="themeStore.theme === 'dark'" :size="20" />
            <Moon v-else :size="20" />
          </button>
          <RouterLink class="icon-button notification-link" :to="{ name: 'ManagementNotifications' }" aria-label="通知中心">
            <Bell :size="20" />
            <span v-if="unreadCount" class="notification-count">{{ unreadCount }}</span>
          </RouterLink>
          <UserMenu compact />
        </div>
      </header>

      <main id="management-main" class="management-main" tabindex="-1">
        <RouterView v-slot="{ Component, route }">
          <component :is="Component" :key="route.path" @notifications-updated="loadNotifications" />
        </RouterView>
      </main>
    </div>
  </div>
</template>

<script>
import { RouterLink, RouterView } from 'vue-router'
import {
  Activity,
  Bell,
  Database,
  Gauge,
  HardDriveUpload,
  Map,
  Menu,
  Moon,
  Network,
  Sun,
  X
} from '@lucide/vue'
import UserMenu from '../UserMenu.vue'
import { useAuthStore } from '@/store/auth'
import { useThemeStore } from '@/store/theme'
import { managementApi } from '@/utils/managementApi'
import { CAPABILITIES, hasCapability } from '@/utils/permissions.mjs'

export default {
  name: 'ManagementLayout',
  components: {
    RouterLink,
    RouterView,
    UserMenu,
    Bell,
    Map,
    Menu,
    Moon,
    Sun,
    X
  },
  setup() {
    const authStore = useAuthStore()
    const themeStore = useThemeStore()
    return { authStore, themeStore }
  },
  data() {
    return {
      drawerOpen: false,
      unreadCount: 0,
      allNavItems: [
        { name: 'ManagementOverview', label: '总览', icon: Activity },
        { name: 'ManagementProjects', label: '项目', icon: Network },
        { name: 'ManagementDevices', label: '设备', icon: Database },
        { name: 'ManagementAssistants', label: '传输助手', icon: HardDriveUpload },
        { name: 'ManagementAssistantMonitor', label: '助手监控', icon: Gauge, capability: CAPABILITIES.ASSISTANT_MONITOR },
        { name: 'ManagementServerCapacity', label: '服务器容量', icon: Gauge, capability: CAPABILITIES.SERVER_CAPACITY_READ },
        { name: 'ManagementBatches', label: '接入记录', icon: Activity },
        { name: 'ManagementNotifications', label: '通知中心', icon: Bell }
      ]
    }
  },
  computed: {
    navItems() {
      return this.allNavItems.filter(item => !item.capability || hasCapability(this.authStore.userInfo, item.capability))
    },
    routeTitle() {
      return this.$route.meta.title || '管理平台'
    },
    themeLabel() {
      return this.themeStore.theme === 'dark' ? '切换到浅色主题' : '切换到深色主题'
    }
  },
  watch: {
    '$route.fullPath'() {
      this.drawerOpen = false
      this.$nextTick(() => {
        document.getElementById('management-main')?.focus({ preventScroll: true })
      })
    }
  },
  created() {
    this.loadCurrentUser()
    this.loadNotifications()
  },
  methods: {
    isNavItemActive(name) {
      const current = this.$route.name
      if (current === name) return true
      const childRoutes = {
        ManagementProjects: ['ManagementProjectDetail'],
        ManagementDevices: ['ManagementDeviceDetail']
      }
      return (childRoutes[name] || []).includes(current)
    },
    async loadCurrentUser() {
      if (!this.authStore.hasRouteAccess || this.authStore.userInfo) return
      try {
        await this.authStore.fetchUserInfo()
      } catch {
        // Route guards and API responses remain the source of truth for access denial.
      }
    },
    async loadNotifications() {
      try {
        const { data } = await managementApi.notifications()
        this.unreadCount = data.filter(item => !item.read_at).length
      } catch {
        this.unreadCount = 0
      }
    }
  }
}
</script>
